import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "./InvestigationDashboard.css";
import axios from "axios";
import { MdTimeline, MdChecklist, MdDescription, MdSearch, MdBalance } from "react-icons/md";
import { FaFolderOpen, FaUsers, FaUserShield } from "react-icons/fa";

const DashboardButton = ({ label, icon, count, onClick, loading, disabled }) => {
    const isProcessed = Number(count) > 0; // ✅ treat "processed" as count > 0
    const isDisabled = Boolean(loading || disabled);

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isDisabled}
            style={{
                width: "100%",
                boxSizing: "border-box",
                backgroundColor: "#f4f8fb",
                padding: "30px 18px",
                borderRadius: "12px",
                border: "none",
                cursor: isDisabled ? "not-allowed" : "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                opacity: disabled && !loading ? 0.65 : 1,
                position: "relative", // ✅ allows top-right status dot if you want later
            }}
            aria-busy={loading ? "true" : "false"}
            aria-disabled={isDisabled ? "true" : "false"}
        >
            <div style={{ height: 40, display: "grid", placeItems: "center" }}>
                {loading ? <div className="spinner"></div> : icon}
            </div>

            <div style={{ marginTop: 12, fontSize: 16, fontWeight: 600, color: "#094268" }}>
                {label}
            </div>

            {/* ✅ status indicator row (replaces count) */}
            <div
                style={{
                    marginTop: 6,
                    minHeight: 14, // ✅ keeps alignment consistent
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {isProcessed ? (
                    <span
                        aria-label="Completed"
                        title="Completed"
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: "#22c55e", // green
                            boxShadow: "0 0 0 2px rgba(34,197,94,0.15)",
                            display: "inline-block",
                        }}
                    />
                ) : (
                    // placeholder to preserve spacing
                    <span style={{ width: 10, height: 10, display: "inline-block" }} />
                )}
            </div>
        </button>
    );
};


const InvestigationDashboardPage = ({ API_URL }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const fir = location.state?.fir;
    const [fullFIR, setFullFIR] = useState(location.state?.fullFIR || null);

    const firId = useMemo(() => {
        const src = fullFIR ?? fir;
        return src?.id || src?.firId || src || null;
    }, [fir, fullFIR]);

    const [complainantName, setComplainantName] = useState("");
    const [caseType, setCaseType] = useState("");

    const [isLoadingFIR, setIsLoadingFIR] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingButton, setLoadingButton] = useState(null);

    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [showCSConfirmation, setShowCSConfirmation] = useState(false);
    const [csAction, setCsAction] = useState(null); // "generate" | "verify"

    const [isViewComplaintPopupOpen, setIsViewComplaintPopupOpen] = useState(false);
    const [isLoadingComplaint, setIsLoadingComplaint] = useState(false);

    const anyButtonRunning = Boolean(loadingButton) || isLoading || isLoadingComplaint; // isLoading already used for “Please wait…” :contentReference[oaicite:2]{index=2}
    const disableIfOtherRunning = (key) => anyButtonRunning && loadingButton !== key;

    const goToLogin = () => navigate("/login", { replace: true });

    const getPreferredLang = () => {
        const raw = (localStorage.getItem("language") || "en").toLowerCase().trim();

        // Map common values to keys used in the dictionary
        const map = {
            english: "en",
            en: "en",
            hindi: "hi",
            hi: "hi",
            gujarati: "gu",
            gu: "gu",
            marathi: "mr",
            mr: "mr",
            bengali: "bn",
            বাংলা: "bn",
            bn: "bn",
            telugu: "te",
            te: "te",
            tamil: "ta",
            ta: "ta",
            kannada: "kn",
            kn: "kn",
            malayalam: "ml",
            ml: "ml",
            punjabi: "pa",
            pa: "pa",
        };

        return map[raw] || "en";
    };

    const SECTION_INFO = {
        events: {
            title: "Extract Timeline",
            text:
                "This will extract and organize all key events related to the case, including dates, times, and locations, to build a clear sequence of what happened.",
        },
        tasks: {
            title: "Identify Tasks",
            text:
                "This will identify the investigation steps, helping you move the case forward.",
        },
        evidences: {
            title: "Identify Evidence",
            text:
                "This will identify possible evidence in the case that may support the investigation.",
        },
        witnesses: {
            title: "Identify Witnesses",
            text:
                "This will identify potential witnesses connected to the case whose statements may be important for the investigation.",
        },
        interrogations: {
            title: "Prepare Accused Questions",
            text:
                "This will generate interrogation questions and investigative angles to help examine the role, intent, and actions of the accused persons.",
        },
        statements: {
            title: "Analyze Statements",
            text:
                "This will analyze recorded statements and suggest follow-up questions to clarify facts, uncover gaps, or probe deeper into important details.",
        },
        discrepancies: {
            title: "Find Discrepancies",
            text:
                "This will detect inconsistencies between statements, evidence, and events, helping you identify contradictions or doubtful areas in the case.",
        },
        chargesheet: {
            title: "Generate Chargesheet",
            text:
                "This will generate a chargesheet based on the case diary.",
        },
    };

    // Preferred language keys (suggested):
    // "en", "hi", "gu", "mr", "bn", "te", "ta", "kn", "ml", "pa"

    const SECTION_INFO_I18N = {
        en: {
            events: {
                title: "Extract Timeline",
                text:
                    "This will extract and organize all key events related to the case, including dates, times, and locations, to build a clear sequence of what happened. The process will take about a minute.",
            },
            tasks: {
                title: "Identify Tasks",
                text:
                    "This will identify the investigation steps, helping you move the case forward. The process will take about a minute.",
            },
            evidences: {
                title: "Identify Evidence",
                text:
                    "This will identify possible evidence in the case that may support the investigation. The process will take about a minute.",
            },
            witnesses: {
                title: "Identify Witnesses",
                text:
                    "This will identify potential witnesses connected to the case whose statements may be important for the investigation. The process will take about a minute.",
            },
            interrogations: {
                title: "Prepare Accused Questions",
                text:
                    "This will generate interrogation questions and investigative angles to help examine the role, intent, and actions of the accused persons. The process will take about a minute.",
            },
            statements: {
                title: "Analyze Statements",
                text:
                    "This will analyze recorded statements and suggest follow-up questions to clarify facts, uncover gaps, or probe deeper into important details. The process will take about a minute.",
            },
            discrepancies: {
                title: "Find Discrepancies",
                text:
                    "This will detect inconsistencies between statements, evidence, and events, helping you identify contradictions or doubtful areas in the case. The process will take about a minute.",
            },
            chargesheet: {
                title: "Generate Chargesheet",
                text:
                    "This will generate a chargesheet from the case diary. The process will take about 30 minutes.",
            },
        },

        hi: {
            events: {
                title: "समयरेखा निकालें",
                text:
                    "यह मामले से जुड़े सभी प्रमुख घटनाक्रमों को तिथि, समय और स्थान सहित निकालकर व्यवस्थित करेगा, ताकि क्या हुआ इसका स्पष्ट क्रम बन सके। इस प्रक्रिया में लगभग एक मिनट लगेगा।",
            },
            tasks: {
                title: "कार्य पहचानें",
                text:
                    "यह जांच के आवश्यक कदमों की पहचान करेगा, जिससे आप केस को आगे बढ़ा सकें। इस प्रक्रिया में लगभग एक मिनट लगेगा।",
            },
            evidences: {
                title: "साक्ष्य पहचानें",
                text:
                    "यह मामले में संभावित साक्ष्यों की पहचान करेगा जो जांच को समर्थन दे सकते हैं। इस प्रक्रिया में लगभग एक मिनट लगेगा।",
            },
            witnesses: {
                title: "गवाह पहचानें",
                text:
                    "यह मामले से जुड़े संभावित गवाहों की पहचान करेगा जिनके बयान जांच के लिए महत्वपूर्ण हो सकते हैं। इस प्रक्रिया में लगभग एक मिनट लगेगा।",
            },
            interrogations: {
                title: "आरोपी से पूछताछ तैयारी",
                text:
                    "यह आरोपी की भूमिका, मंशा और कार्यों की जांच में मदद के लिए पूछताछ प्रश्न और जांच के पहलू तैयार करेगा। इस प्रक्रिया में लगभग एक मिनट लगेगा।",
            },
            statements: {
                title: "बयान विश्लेषण करें",
                text:
                    "यह दर्ज बयानों का विश्लेषण करेगा और तथ्यों को स्पष्ट करने, कमियां खोजने या महत्वपूर्ण बिंदुओं पर गहराई से पूछने के लिए पूरक प्रश्न सुझाएगा। इस प्रक्रिया में लगभग एक मिनट लगेगा।",
            },
            discrepancies: {
                title: "विसंगतियां खोजें",
                text:
                    "यह बयानों, साक्ष्यों और घटनाओं के बीच असंगतियां पहचानकर विरोधाभास या संदिग्ध बिंदुओं को उजागर करेगा। इस प्रक्रिया में लगभग एक मिनट लगेगा।",
            },
            chargesheet: {
                title: "चार्जशीट बनाएं",
                text:
                    "यह केस डायरी से चार्जशीट तैयार करेगा। इस प्रक्रिया में लगभग 30 मिनट लगेंगे।",
            },
        },

        gu: {
            events: {
                title: "ટાઇમલાઇન કાઢો",
                text:
                    "આ કેસ સંબંધિત તમામ મુખ્ય ઘટનાઓને તારીખ, સમય અને સ્થળ સાથે કાઢીને ગોઠવશે, જેથી શું બન્યું તેનું સ્પષ્ટ ક્રમ બને. આ પ્રક્રિયામાં અંદાજે એક મિનિટ લાગશે.",
            },
            tasks: {
                title: "કાર્યો ઓળખો",
                text:
                    "આ તપાસ માટે જરૂરી પગલાં ઓળખશે, જેથી તમે કેસ આગળ વધારી શકો. આ પ્રક્રિયામાં અંદાજે એક મિનિટ લાગશે.",
            },
            evidences: {
                title: "પુરાવા ઓળખો",
                text:
                    "આ કેસમાં તપાસને મદદરૂપ બની શકે એવા સંભવિત પુરાવા ઓળખશે. આ પ્રક્રિયામાં અંદાજે એક મિનિટ લાગશે.",
            },
            witnesses: {
                title: "સાક્ષીઓ ઓળખો",
                text:
                    "આ કેસ સાથે જોડાયેલા સંભવિત સાક્ષીઓ ઓળખશે, જેમના નિવેદનો તપાસ માટે મહત્વના હોઈ શકે. આ પ્રક્રિયામાં અંદાજે એક મિનિટ લાગશે.",
            },
            interrogations: {
                title: "આરોપી પ્રશ્નો તૈયાર કરો",
                text:
                    "આ આરોપીની ભૂમિકા, ઇરાદો અને ક્રિયાઓ તપાસવા માટે પૂછપરછના પ્રશ્નો અને તપાસના દૃષ્ટિકોણ તૈયાર કરશે. આ પ્રક્રિયામાં અંદાજે એક મિનિટ લાગશે.",
            },
            statements: {
                title: "નિવેદનો વિશ્લેષણ કરો",
                text:
                    "આ નોંધાયેલા નિવેદનોનું વિશ્લેષણ કરશે અને તથ્યો સ્પષ્ટ કરવા, ખામીઓ શોધવા અથવા મહત્વના મુદ્દાઓ પર વધુ પૂછપરછ માટે અનુગામી પ્રશ્નો સૂચવશે. આ પ્રક્રિયામાં અંદાજે એક મિનિટ લાગશે.",
            },
            discrepancies: {
                title: "અસંગતતાઓ શોધો",
                text:
                    "આ નિવેદનો, પુરાવા અને ઘટનાઓ વચ્ચેની અસંગતતાઓ શોધીને વિરોધાભાસ અથવા શંકાસ્પદ મુદ્દાઓ ઓળખવામાં મદદ કરશે. આ પ્રક્રિયામાં અંદાજે એક મિનિટ લાગશે.",
            },
            chargesheet: {
                title: "ચાર્જશીટ બનાવો",
                text:
                    "આ કેસ ડાયરીમાંથી ચાર્જશીટ તૈયાર કરશે. આ પ્રક્રિયામાં અંદાજે 30 મિનિટ લાગશે.",
            },
        },

        mr: {
            events: {
                title: "टाइमलाइन काढा",
                text:
                    "हे प्रकरणाशी संबंधित सर्व महत्त्वाच्या घटना तारीख, वेळ आणि ठिकाणासह काढून मांडेल, त्यामुळे काय घडलं याचा स्पष्ट क्रम तयार होईल. या प्रक्रियेला साधारण एक मिनिट लागेल.",
            },
            tasks: {
                title: "कामे ओळखा",
                text:
                    "हे तपासातील आवश्यक पावले ओळखेल, ज्यामुळे तुम्ही केस पुढे नेऊ शकाल. या प्रक्रियेला साधारण एक मिनिट लागेल.",
            },
            evidences: {
                title: "पुरावे ओळखा",
                text:
                    "हे तपासाला मदत करू शकणारे प्रकरणातील संभाव्य पुरावे ओळखेल. या प्रक्रियेला साधारण एक मिनिट लागेल.",
            },
            witnesses: {
                title: "साक्षीदार ओळखा",
                text:
                    "हे प्रकरणाशी संबंधित संभाव्य साक्षीदार ओळखेल, ज्यांची जबाब तपासासाठी महत्त्वाची असू शकतात. या प्रक्रियेला साधारण एक मिनिट लागेल.",
            },
            interrogations: {
                title: "आरोपी प्रश्न तयार करा",
                text:
                    "हे आरोपींची भूमिका, हेतू आणि कृती तपासण्यासाठी चौकशीचे प्रश्न आणि तपासाचे दृष्टीकोन तयार करेल. या प्रक्रियेला साधारण एक मिनिट लागेल.",
            },
            statements: {
                title: "जबाबांचे विश्लेषण करा",
                text:
                    "हे नोंदवलेल्या जबाबांचे विश्लेषण करून तथ्य स्पष्ट करण्यासाठी, त्रुटी शोधण्यासाठी किंवा महत्त्वाच्या मुद्द्यांवर अधिक विचारण्यासाठी पूरक प्रश्न सुचवेल. या प्रक्रियेला साधारण एक मिनिट लागेल.",
            },
            discrepancies: {
                title: "विसंगती शोधा",
                text:
                    "हे जबाब, पुरावे आणि घटनांमधील विसंगती शोधून विरोधाभास किंवा संशयास्पद मुद्दे ओळखण्यास मदत करेल. या प्रक्रियेला साधारण एक मिनिट लागेल.",
            },
            chargesheet: {
                title: "चार्जशीट तयार करा",
                text:
                    "हे केस डायरीमधून चार्जशीट तयार करेल. या प्रक्रियेला साधारण 30 मिनिटे लागतील.",
            },
        },

        bn: {
            events: {
                title: "টাইমলাইন বের করুন",
                text:
                    "এটি মামলার সাথে সম্পর্কিত সব গুরুত্বপূর্ণ ঘটনা তারিখ, সময় ও স্থানসহ বের করে গুছিয়ে দেবে, যাতে কী ঘটেছে তার একটি পরিষ্কার ক্রম তৈরি হয়। এই প্রক্রিয়ায় প্রায় এক মিনিট সময় লাগবে।",
            },
            tasks: {
                title: "কাজ চিহ্নিত করুন",
                text:
                    "এটি তদন্তের প্রয়োজনীয় পদক্ষেপগুলো চিহ্নিত করবে, যাতে আপনি মামলাটি এগিয়ে নিতে পারেন। এই প্রক্রিয়ায় প্রায় এক মিনিট সময় লাগবে।",
            },
            evidences: {
                title: "প্রমাণ চিহ্নিত করুন",
                text:
                    "এটি তদন্তকে সহায়তা করতে পারে এমন মামলার সম্ভাব্য প্রমাণ চিহ্নিত করবে। এই প্রক্রিয়ায় প্রায় এক মিনিট সময় লাগবে।",
            },
            witnesses: {
                title: "সাক্ষী চিহ্নিত করুন",
                text:
                    "এটি মামলার সাথে যুক্ত সম্ভাব্য সাক্ষীদের চিহ্নিত করবে, যাদের জবানবন্দি তদন্তের জন্য গুরুত্বপূর্ণ হতে পারে। এই প্রক্রিয়ায় প্রায় এক মিনিট সময় লাগবে।",
            },
            interrogations: {
                title: "আসামি প্রশ্ন প্রস্তুত করুন",
                text:
                    "এটি আসামিদের ভূমিকা, উদ্দেশ্য এবং কাজকর্ম যাচাই করতে জিজ্ঞাসাবাদের প্রশ্ন ও তদন্তের দৃষ্টিভঙ্গি তৈরি করবে। এই প্রক্রিয়ায় প্রায় এক মিনিট সময় লাগবে।",
            },
            statements: {
                title: "জবানবন্দি বিশ্লেষণ করুন",
                text:
                    "এটি নথিভুক্ত জবানবন্দি বিশ্লেষণ করে তথ্য স্পষ্ট করা, ঘাটতি খোঁজা বা গুরুত্বপূর্ণ বিষয়ে আরও গভীরে যেতে অনুস্মারক প্রশ্ন প্রস্তাব করবে। এই প্রক্রিয়ায় প্রায় এক মিনিট সময় লাগবে।",
            },
            discrepancies: {
                title: "অমিল খুঁজুন",
                text:
                    "এটি জবানবন্দি, প্রমাণ এবং ঘটনার মধ্যে অমিল ধরতে সাহায্য করবে, যাতে বিরোধ বা সন্দেহজনক বিষয় চিহ্নিত করা যায়। এই প্রক্রিয়ায় প্রায় এক মিনিট সময় লাগবে।",
            },
            chargesheet: {
                title: "চার্জশিট তৈরি করুন",
                text:
                    "এটি কেস ডায়েরি থেকে চার্জশিট তৈরি করবে। এই প্রক্রিয়ায় প্রায় ৩০ মিনিট সময় লাগবে।",
            },
        },

        te: {
            events: {
                title: "టైమ్‌లైన్ తీసుకోండి",
                text:
                    "ఇది కేసుకు సంబంధించిన కీలక ఘటనలను తేదీ, సమయం, స్థలం సహా తీసుకుని క్రమబద్ధీకరిస్తుంది, తద్వారా ఏమి జరిగిందో స్పష్టమైన వరుసగా తెలుస్తుంది. ఈ ప్రక్రియకు సుమారు ఒక నిమిషం పడుతుంది.",
            },
            tasks: {
                title: "పనులు గుర్తించండి",
                text:
                    "ఇది దర్యాప్తులో చేయాల్సిన అవసరమైన చర్యలను గుర్తిస్తుంది, తద్వారా మీరు కేసును ముందుకు తీసుకెళ్లగలరు. ఈ ప్రక్రియకు సుమారు ఒక నిమిషం పడుతుంది.",
            },
            evidences: {
                title: "సాక్ష్యాలు గుర్తించండి",
                text:
                    "ఇది దర్యాప్తుకు సహాయపడే అవకాశమున్న కేసులోని సాక్ష్యాలను గుర్తిస్తుంది. ఈ ప్రక్రియకు సుమారు ఒక నిమిషం పడుతుంది.",
            },
            witnesses: {
                title: "సాక్షులను గుర్తించండి",
                text:
                    "ఇది కేసుకు సంబంధించిన సంభావ్య సాక్షులను గుర్తిస్తుంది, వారి వాంగ్మూలాలు దర్యాప్తుకు ముఖ్యంగా ఉండవచ్చు. ఈ ప్రక్రియకు సుమారు ఒక నిమిషం పడుతుంది.",
            },
            interrogations: {
                title: "నిందిత ప్రశ్నలు సిద్ధం చేయండి",
                text:
                    "ఇది నిందితుల పాత్ర, ఉద్దేశ్యం, చర్యలను పరిశీలించేందుకు విచారణ ప్రశ్నలు మరియు దర్యాప్తు కోణాలను రూపొందిస్తుంది. ఈ ప్రక్రియకు సుమారు ఒక నిమిషం పడుతుంది.",
            },
            statements: {
                title: "వాంగ్మూలాలు విశ్లేషించండి",
                text:
                    "ఇది నమోదు చేసిన వాంగ్మూలాలను విశ్లేషించి వాస్తవాలు స్పష్టంగా చేయడానికి, లోపాలు గుర్తించడానికి లేదా కీలక అంశాల్లో లోతుగా అడగడానికి అనుసరణ ప్రశ్నలు సూచిస్తుంది. ఈ ప్రక్రియకు సుమారు ఒక నిమిషం పడుతుంది.",
            },
            discrepancies: {
                title: "అసంగతులు కనుగొనండి",
                text:
                    "ఇది వాంగ్మూలాలు, సాక్ష్యాలు, ఘటనల మధ్య ఉన్న అసంగతులను గుర్తించి విరుద్ధతలు లేదా అనుమానాస్పద అంశాలను కనిపెట్టడానికి సహాయం చేస్తుంది. ఈ ప్రక్రియకు సుమారు ఒక నిమిషం పడుతుంది.",
            },
            chargesheet: {
                title: "చార్జ్‌షీట్ తయారు చేయండి",
                text:
                    "ఇది కేసు డైరీ నుండి చార్జ్‌షీట్‌ను తయారు చేస్తుంది. ఈ ప్రక్రియకు సుమారు 30 నిమిషాలు పడుతుంది.",
            },
        },

        ta: {
            events: {
                title: "காலவரிசை எடுக்கவும்",
                text:
                    "இது வழக்குடன் தொடர்பான முக்கிய நிகழ்வுகளை தேதி, நேரம், இடம் உடன் எடுத்து ஒழுங்குபடுத்தும், இதனால் நடந்தது என்ன என்பது தெளிவான வரிசையாக கிடைக்கும். இந்த செயலுக்கு சுமார் ஒரு நிமிடம் ஆகும்.",
            },
            tasks: {
                title: "பணிகளை கண்டறியவும்",
                text:
                    "இது விசாரணையில் செய்ய வேண்டிய நடவடிக்கைகளை கண்டறிந்து, வழக்கை முன்னேற்ற உதவும். இந்த செயலுக்கு சுமார் ஒரு நிமிடம் ஆகும்.",
            },
            evidences: {
                title: "ஆதாரம் கண்டறியவும்",
                text:
                    "இது விசாரணைக்கு ஆதரவாக இருக்கக்கூடிய வழக்கின் சாத்தியமான ஆதாரங்களை கண்டறியும். இந்த செயலுக்கு சுமார் ஒரு நிமிடம் ஆகும்.",
            },
            witnesses: {
                title: "சாட்சியாளர்களை கண்டறியவும்",
                text:
                    "இது வழக்குடன் தொடர்புடைய சாத்தியமான சாட்சியாளர்களை கண்டறியும்; அவர்களின் வாக்குமூலம் விசாரணைக்கு முக்கியமாக இருக்கலாம். இந்த செயலுக்கு சுமார் ஒரு நிமிடம் ஆகும்.",
            },
            interrogations: {
                title: "குற்றச்சாட்டாளருக்கான கேள்விகள்",
                text:
                    "இது குற்றச்சாட்டாளர்களின் பங்கு, நோக்கம், செயல்களை பரிசோதிக்க விசாரணை கேள்விகள் மற்றும் விசாரணை கோணங்களை உருவாக்கும். இந்த செயலுக்கு சுமார் ஒரு நிமிடம் ஆகும்.",
            },
            statements: {
                title: "வாக்குமூலங்களை பகுப்பாய்வு",
                text:
                    "இது பதிவு செய்யப்பட்ட வாக்குமூலங்களை பகுப்பாய்வு செய்து உண்மைகளை தெளிவுபடுத்த, இடைவெளிகளை கண்டறிய, அல்லது முக்கிய விஷயங்களில் ஆழமாக கேட்க தொடர்ச்சி கேள்விகளை பரிந்துரைக்கும். இந்த செயலுக்கு சுமார் ஒரு நிமிடம் ஆகும்.",
            },
            discrepancies: {
                title: "முரண்பாடுகளை கண்டறியவும்",
                text:
                    "இது வாக்குமூல்கள், ஆதாரங்கள், நிகழ்வுகள் இடையிலான முரண்பாடுகளை கண்டறிந்து, எதிர்மறைகள் அல்லது சந்தேகமான பகுதிகளை அடையாளம் காண உதவும். இந்த செயலுக்கு சுமார் ஒரு நிமிடம் ஆகும்.",
            },
            chargesheet: {
                title: "சார்ஜ்ஷீட் உருவாக்கவும்",
                text:
                    "இது கேஸ் டைரியிலிருந்து சார்ஜ்ஷீட்டை உருவாக்கும். இந்த செயலுக்கு சுமார் 30 நிமிடங்கள் ஆகும்.",
            },
        },

        kn: {
            events: {
                title: "ಟೈಮ್‌ಲೈನ್ ಹೊರತೆಗೆಯಿರಿ",
                text:
                    "ಇದು ಪ್ರಕರಣಕ್ಕೆ ಸಂಬಂಧಿಸಿದ ಪ್ರಮುಖ ಘಟನೆಗಳನ್ನು ದಿನಾಂಕ, ಸಮಯ ಮತ್ತು ಸ್ಥಳದೊಂದಿಗೆ ಹೊರತೆಗೆದು ಕ್ರಮಬದ್ಧಗೊಳಿಸುತ್ತದೆ, ಏನು ನಡೆದಿದೆ ಎಂಬುದಕ್ಕೆ ಸ್ಪಷ್ಟ ಕ್ರಮ ಸಿಗುತ್ತದೆ. ಈ ಪ್ರಕ್ರಿಯೆಗೆ ಸುಮಾರು ಒಂದು ನಿಮಿಷ ಬೇಕಾಗುತ್ತದೆ.",
            },
            tasks: {
                title: "ಕಾರ್ಯಗಳನ್ನು ಗುರುತಿಸಿ",
                text:
                    "ಇದು ತನಿಖೆಯಲ್ಲಿ ಮಾಡಬೇಕಾದ ಅಗತ್ಯ ಕ್ರಮಗಳನ್ನು ಗುರುತಿಸಿ, ಪ್ರಕರಣವನ್ನು ಮುಂದಕ್ಕೆ ಸಾಗಿಸಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ. ಈ ಪ್ರಕ್ರಿಯೆಗೆ ಸುಮಾರು ಒಂದು ನಿಮಿಷ ಬೇಕಾಗುತ್ತದೆ.",
            },
            evidences: {
                title: "ಸಾಕ್ಷ್ಯ ಗುರುತಿಸಿ",
                text:
                    "ಇದು ತನಿಖೆಗೆ ಬೆಂಬಲವಾಗಬಹುದಾದ ಪ್ರಕರಣದ ಸಾಧ್ಯ ಸಾಕ್ಷ್ಯವನ್ನು ಗುರುತಿಸುತ್ತದೆ. ಈ ಪ್ರಕ್ರಿಯೆಗೆ ಸುಮಾರು ಒಂದು ನಿಮಿಷ ಬೇಕಾಗುತ್ತದೆ.",
            },
            witnesses: {
                title: "ಸಾಕ್ಷಿದಾರರನ್ನು ಗುರುತಿಸಿ",
                text:
                    "ಇದು ಪ್ರಕರಣಕ್ಕೆ ಸಂಬಂಧಿಸಿದ ಸಾಧ್ಯ ಸಾಕ್ಷಿದಾರರನ್ನು ಗುರುತಿಸುತ್ತದೆ; ಅವರ ಹೇಳಿಕೆಗಳು ತನಿಖೆಗೆ ಮುಖ್ಯವಾಗಬಹುದು. ಈ ಪ್ರಕ್ರಿಯೆಗೆ ಸುಮಾರು ಒಂದು ನಿಮಿಷ ಬೇಕಾಗುತ್ತದೆ.",
            },
            interrogations: {
                title: "ಆರೋಪಿ ಪ್ರಶ್ನೆಗಳು ತಯಾರು",
                text:
                    "ಇದು ಆರೋಪಿತರ ಪಾತ್ರ, ಉದ್ದೇಶ ಮತ್ತು ಕ್ರಿಯೆಗಳನ್ನು ಪರಿಶೀಲಿಸಲು ವಿಚಾರಣೆ ಪ್ರಶ್ನೆಗಳು ಮತ್ತು ತನಿಖಾ ದೃಷ್ಟಿಕೋಣಗಳನ್ನು ರಚಿಸುತ್ತದೆ. ಈ ಪ್ರಕ್ರಿಯೆಗೆ ಸುಮಾರು ಒಂದು ನಿಮಿಷ ಬೇಕಾಗುತ್ತದೆ.",
            },
            statements: {
                title: "ಹೇಳಿಕೆಗಳನ್ನು ವಿಶ್ಲೇಷಿಸಿ",
                text:
                    "ಇದು ದಾಖಲಾದ ಹೇಳಿಕೆಗಳನ್ನು ವಿಶ್ಲೇಷಿಸಿ ವಾಸ್ತವಗಳನ್ನು ಸ್ಪಷ್ಟಪಡಿಸಲು, ಖಾಲಿತನಗಳನ್ನು ಕಂಡುಹಿಡಿಯಲು ಅಥವಾ ಪ್ರಮುಖ ಅಂಶಗಳಲ್ಲಿ ಆಳವಾಗಿ ಕೇಳಲು ಮುಂದಿನ ಪ್ರಶ್ನೆಗಳನ್ನು ಸೂಚಿಸುತ್ತದೆ. ಈ ಪ್ರಕ್ರಿಯೆಗೆ ಸುಮಾರು ಒಂದು ನಿಮಿಷ ಬೇಕಾಗುತ್ತದೆ.",
            },
            discrepancies: {
                title: "ಅಸಂಗತತೆ ಕಂಡುಹಿಡಿಯಿರಿ",
                text:
                    "ಇದು ಹೇಳಿಕೆಗಳು, ಸಾಕ್ಷ್ಯ ಮತ್ತು ಘಟನೆಗಳ ನಡುವೆ ಅಸಂಗತತೆಗಳನ್ನು ಪತ್ತೆಹಚ್ಚಿ, ವಿರೋಧಾಭಾಸಗಳು ಅಥವಾ ಅನುಮಾನಾಸ್ಪದ ಭಾಗಗಳನ್ನು ಗುರುತಿಸಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ. ಈ ಪ್ರಕ್ರಿಯೆಗೆ ಸುಮಾರು ಒಂದು ನಿಮಿಷ ಬೇಕಾಗುತ್ತದೆ.",
            },
            chargesheet: {
                title: "ಚಾರ್ಜ್‌ಶೀಟ್ ರಚಿಸಿ",
                text:
                    "ಇದು ಕೇಸ್ ಡೈರಿಯಿಂದ ಚಾರ್ಜ್‌ಶೀಟ್ ರಚಿಸುತ್ತದೆ. ಈ ಪ್ರಕ್ರಿಯೆಗೆ ಸುಮಾರು 30 ನಿಮಿಷಗಳು ಬೇಕಾಗುತ್ತವೆ.",
            },
        },

        ml: {
            events: {
                title: "ടൈംലൈൻ എടുക്കുക",
                text:
                    "കേസുമായി ബന്ധപ്പെട്ട പ്രധാന സംഭവങ്ങളെ തീയതി, സമയം, സ്ഥലം എന്നിവയോടെ എടുത്ത് ക്രമീകരിക്കും, എന്താണ് സംഭവിച്ചതെന്ന് വ്യക്തമായ ക്രമത്തിൽ കാണാൻ സഹായിക്കും. ഈ പ്രക്രിയയ്ക്ക് ഏകദേശം ഒരു മിനിറ്റ് എടുക്കും.",
            },
            tasks: {
                title: "ടാസ്കുകൾ കണ്ടെത്തുക",
                text:
                    "അന്വേഷണത്തിൽ ചെയ്യേണ്ട ആവശ്യമായ നടപടികൾ കണ്ടെത്തി, കേസ് മുന്നോട്ട് കൊണ്ടുപോകാൻ സഹായിക്കും. ഈ പ്രക്രിയയ്ക്ക് ഏകദേശം ഒരു മിനിറ്റ് എടുക്കും.",
            },
            evidences: {
                title: "തെളിവുകൾ കണ്ടെത്തുക",
                text:
                    "അന്വേഷണത്തെ പിന്തുണയ്ക്കാൻ കഴിയുന്ന കേസിലെ സാധ്യതയുള്ള തെളിവുകൾ കണ്ടെത്തും. ഈ പ്രക്രിയയ്ക്ക് ഏകദേശം ഒരു മിനിറ്റ് എടുക്കും.",
            },
            witnesses: {
                title: "സാക്ഷികളെ കണ്ടെത്തുക",
                text:
                    "കേസുമായി ബന്ധപ്പെട്ട സാധ്യതയുള്ള സാക്ഷികളെ കണ്ടെത്തും; അവരുടെ മൊഴികൾ അന്വേഷണത്തിന് പ്രധാനപ്പെട്ടവയായിരിക്കാം. ഈ പ്രക്രിയയ്ക്ക് ഏകദേശം ഒരു മിനിറ്റ് എടുക്കും.",
            },
            interrogations: {
                title: "പ്രതി ചോദ്യങ്ങൾ തയ്യാറാക്കുക",
                text:
                    "പ്രതികളുടെ പങ്ക്, ഉദ്ദേശ്യം, പ്രവർത്തികൾ എന്നിവ പരിശോധിക്കാൻ ചോദ്യം ചെയ്യൽ ചോദ്യങ്ങളും അന്വേഷണ കോണുകളും സൃഷ്ടിക്കും. ഈ പ്രക്രിയയ്ക്ക് ഏകദേശം ഒരു മിനിറ്റ് എടുക്കും.",
            },
            statements: {
                title: "മൊഴികൾ വിശകലനം ചെയ്യുക",
                text:
                    "രേഖപ്പെടുത്തിയ മൊഴികൾ വിശകലനം ചെയ്ത് വസ്തുതകൾ വ്യക്തപ്പെടുത്താനും, കുറവുകൾ കണ്ടെത്താനും, പ്രധാന വിഷയങ്ങളിൽ കൂടുതൽ ആഴത്തിൽ ചോദിക്കാനും തുടർചോദ്യങ്ങൾ നിർദ്ദേശിക്കും. ഈ പ്രക്രിയയ്ക്ക് ഏകദേശം ഒരു മിനിറ്റ് എടുക്കും.",
            },
            discrepancies: {
                title: "വ്യത്യാസങ്ങൾ കണ്ടെത്തുക",
                text:
                    "മൊഴികൾ, തെളിവുകൾ, സംഭവങ്ങൾ എന്നിവയ്ക്കിടയിലെ അസംഗതികൾ കണ്ടെത്തി വിരുദ്ധതകൾ അല്ലെങ്കിൽ സംശയാസ്പദ ഭാഗങ്ങൾ തിരിച്ചറിയാൻ സഹായിക്കും. ഈ പ്രക്രിയയ്ക്ക് ഏകദേശം ഒരു മിനിറ്റ് എടുക്കും.",
            },
            chargesheet: {
                title: "ചാർജ്‌ഷീറ്റ് തയ്യാറാക്കുക",
                text:
                    "ഇത് കേസ് ഡയറിയിൽ നിന്ന് ചാർജ്‌ഷീറ്റ് തയ്യാറാക്കും. ഈ പ്രക്രിയയ്ക്ക് ഏകദേശം 30 മിനിറ്റ് വേണ്ടിവരും.",
            },
        },

        pa: {
            events: {
                title: "ਟਾਈਮਲਾਈਨ ਕੱਢੋ",
                text:
                    "ਇਹ ਕੇਸ ਨਾਲ ਸੰਬੰਧਿਤ ਸਾਰੀਆਂ ਮੁੱਖ ਘਟਨਾਵਾਂ ਨੂੰ ਤਾਰੀਖ, ਸਮਾਂ ਅਤੇ ਥਾਂ ਸਮੇਤ ਕੱਢ ਕੇ ਠੀਕ ਕ੍ਰਮ ਵਿੱਚ ਰੱਖੇਗਾ, ਤਾਂ ਜੋ ਕੀ ਹੋਇਆ ਉਸਦੀ ਸਪੱਸ਼ਟ ਲੜੀ ਬਣ ਸਕੇ। ਇਸ ਪ੍ਰਕਿਰਿਆ ਨੂੰ ਲਗਭਗ ਇੱਕ ਮਿੰਟ ਲੱਗੇਗਾ।",
            },
            tasks: {
                title: "ਟਾਸਕ ਪਛਾਣੋ",
                text:
                    "ਇਹ ਜਾਂਚ ਦੇ ਲੋੜੀਂਦੇ ਕਦਮ ਪਛਾਣੇਗਾ, ਜਿਸ ਨਾਲ ਤੁਸੀਂ ਕੇਸ ਅੱਗੇ ਵਧਾ ਸਕੋਗੇ। ਇਸ ਪ੍ਰਕਿਰਿਆ ਨੂੰ ਲਗਭਗ ਇੱਕ ਮਿੰਟ ਲੱਗੇਗਾ।",
            },
            evidences: {
                title: "ਸਬੂਤ ਪਛਾਣੋ",
                text:
                    "ਇਹ ਕੇਸ ਵਿੱਚ ਸੰਭਾਵਿਤ ਸਬੂਤ ਪਛਾਣੇਗਾ ਜੋ ਜਾਂਚ ਨੂੰ ਸਮਰਥਨ ਦੇ ਸਕਦੇ ਹਨ। ਇਸ ਪ੍ਰਕਿਰਿਆ ਨੂੰ ਲਗਭਗ ਇੱਕ ਮਿੰਟ ਲੱਗੇਗਾ।",
            },
            witnesses: {
                title: "ਗਵਾਹ ਪਛਾਣੋ",
                text:
                    "ਇਹ ਕੇਸ ਨਾਲ ਜੁੜੇ ਸੰਭਾਵਿਤ ਗਵਾਹ ਪਛਾਣੇਗਾ ਜਿਨ੍ਹਾਂ ਦੇ ਬਿਆਨ ਜਾਂਚ ਲਈ ਮਹੱਤਵਪੂਰਨ ਹੋ ਸਕਦੇ ਹਨ। ਇਸ ਪ੍ਰਕਿਰਿਆ ਨੂੰ ਲਗਭਗ ਇੱਕ ਮਿੰਟ ਲੱਗੇਗਾ।",
            },
            interrogations: {
                title: "ਆਰੋਪੀ ਸਵਾਲ ਤਿਆਰ ਕਰੋ",
                text:
                    "ਇਹ ਆਰੋਪੀਆਂ ਦੀ ਭੂਮਿਕਾ, ਇਰਾਦਾ ਅਤੇ ਕਰਤੂਤਾਂ ਦੀ ਜਾਂਚ ਲਈ ਪੁੱਛਗਿੱਛ ਦੇ ਸਵਾਲ ਅਤੇ ਜਾਂਚ ਦੇ ਕੋਣ ਤਿਆਰ ਕਰੇਗਾ। ਇਸ ਪ੍ਰਕਿਰਿਆ ਨੂੰ ਲਗਭਗ ਇੱਕ ਮਿੰਟ ਲੱਗੇਗਾ।",
            },
            statements: {
                title: "ਬਿਆਨ ਵਿਸ਼ਲੇਸ਼ਣ ਕਰੋ",
                text:
                    "ਇਹ ਦਰਜ ਬਿਆਨਾਂ ਦਾ ਵਿਸ਼ਲੇਸ਼ਣ ਕਰਕੇ ਤੱਥ ਸਪੱਸ਼ਟ ਕਰਨ, ਘਾਟਾਂ ਲੱਭਣ ਜਾਂ ਮਹੱਤਵਪੂਰਨ ਗੱਲਾਂ ਉੱਤੇ ਹੋਰ ਡੂੰਘੇ ਸਵਾਲ ਪੁੱਛਣ ਲਈ ਅਗਲੇ ਸਵਾਲ ਸੁਝਾਏਗਾ। ਇਸ ਪ੍ਰਕਿਰਿਆ ਨੂੰ ਲਗਭਗ ਇੱਕ ਮਿੰਟ ਲੱਗੇਗਾ।",
            },
            discrepancies: {
                title: "ਅਸੰਗਤੀਆਂ ਲੱਭੋ",
                text:
                    "ਇਹ ਬਿਆਨਾਂ, ਸਬੂਤਾਂ ਅਤੇ ਘਟਨਾਵਾਂ ਵਿਚਕਾਰ ਅਸੰਗਤੀਆਂ ਪਛਾਣ ਕੇ ਵਿਰੋਧ ਜਾਂ ਸ਼ੱਕ ਵਾਲੇ ਬਿੰਦੂ ਲੱਭਣ ਵਿੱਚ ਮਦਦ ਕਰੇਗਾ। ਇਸ ਪ੍ਰਕਿਰਿਆ ਨੂੰ ਲਗਭਗ ਇੱਕ ਮਿੰਟ ਲੱਗੇਗਾ।",
            },
            chargesheet: {
                title: "ਚਾਰਜਸ਼ੀਟ ਬਣਾਓ",
                text:
                    "ਇਹ ਕੇਸ ਡਾਇਰੀ ਤੋਂ ਚਾਰਜਸ਼ੀਟ ਤਿਆਰ ਕਰੇਗਾ। ਇਸ ਪ੍ਰਕਿਰਿਆ ਨੂੰ ਲਗਭਗ 30 ਮਿੰਟ ਲੱਗਣਗੇ।",
            },
        },
    };

    const CONTINUE_PROMPT_I18N = {
        en: "Do you want to continue?",
        hi: "क्या आप जारी रखना चाहते हैं?",
        gu: "શું તમે આગળ વધવા માંગો છો?",
        mr: "आपण पुढे जायचे आहे का?",
        bn: "আপনি কি এগোতে চান?",
        or: "ଆପଣ ଆଗକୁ ବଢ଼ିବାକୁ ଚାହାଁନ୍ତି କି?",
        te: "మీరు కొనసాగించాలనుకుంటున్నారా?",
        ta: "நீங்கள் தொடர விரும்புகிறீர்களா?",
        kn: "ನೀವು ಮುಂದುವರೆಯಲು ಬಯಸುತ್ತೀರಾ?",
        ml: "നിങ്ങൾ തുടരാൻ ആഗ്രഹിക്കുന്നുണ്ടോ?",
        pa: "ਕੀ ਤੁਸੀਂ ਅੱਗੇ ਵੱਧਣਾ ਚਾਹੁੰਦੇ ਹੋ?",
        ur: "کیا آپ جاری رکھنا چاہتے ہیں؟",
    };

    const [confirmSection, setConfirmSection] = useState({
        open: false,
        endpoint: null,
        title: "",
        text: "",
    });

    const isSectionGenerated = (data, endpoint) => {
        if (!data) return false;

        switch (endpoint) {
            case "events":
                return Array.isArray(data.eventsAndTimeline) && data.eventsAndTimeline.length > 0;

            case "tasks":
                return Array.isArray(data.urgentTasks) && data.urgentTasks.length > 0;

            case "evidences":
                return data.evidenceList && typeof data.evidenceList === "object" && Object.keys(data.evidenceList).length > 0;

            case "witnesses":
                return Array.isArray(data.witnessList) && data.witnessList.length > 0;

            case "interrogations":
                // Your UI always navigates for this endpoint, but "generated" can be based on accusedList presence.
                // If you store generated interrogation Qs separately later, update this.
                return Array.isArray(data.accusedList) && data.accusedList.length > 0;

            case "statements":
                return Array.isArray(data?.investigation?.responses) && data.investigation.responses.length > 0;

            case "discrepancies": {
                const c = data?.investigation?.contradictions?.length ?? 0;
                const i = data?.investigation?.inconsistencies?.length ?? 0;
                return c + i > 0;
            }

            case "chargesheet":
                // Based on your current count logic:
                return Boolean(data?.caseDiary?.bucketFileName);

            default:
                return false;
        }
    };

    const handleSectionClick = async (endpoint) => {
        if (disableIfOtherRunning(endpoint)) return;

        let data = fullFIR;
        if (!data) data = await getFullFIR();
        if (!data) return;

        // If already generated, go directly
        if (isSectionGenerated(data, endpoint)) {
            callApi(endpoint);
            return;
        }

        // Not generated: show localized popup
        const lang = getPreferredLang();
        const dict = SECTION_INFO_I18N[lang] || SECTION_INFO_I18N.en;
        const cfg = dict[endpoint] || SECTION_INFO_I18N.en[endpoint];

        if (!cfg) {
            callApi(endpoint);
            return;
        }

        setConfirmSection({
            open: true,
            endpoint,
            title: cfg.title,
            text: cfg.text,
        });
    };


    const closeSectionPopup = () => {
        setConfirmSection({ open: false, endpoint: null, title: "", text: "" });
    };

    const confirmSectionAndProceed = () => {
        const endpoint = confirmSection.endpoint;
        closeSectionPopup();
        if (endpoint) callApi(endpoint);
    };

    const showErrorPopup = (msg) => {
        setErrorMessage(msg || "An error occurred. Please try after some time.");
        setShowError(true);
    };

    const handleCloseError = () => {
        setShowError(false);
        setErrorMessage("");
    };

    const getFullFIR = async () => {
        try {
            let cachedDate = localStorage.getItem(firId);
            if (cachedDate) {
                setFullFIR(cachedDate);
                localStorage.removeItem(firId);
                return;
            }

            setIsLoading(true);
            setIsLoadingFIR(true);

            const jwtToken = localStorage.getItem("jwtToken");
            if (!jwtToken) {
                showErrorPopup("Session Expired. Please log in again.");
                goToLogin();
                return null;
            }

            const response = await axios.get(`${API_URL}/fir/${firId}`, {
                headers: { Authorization: `Bearer ${jwtToken}` },
            });

            if (response.status === 200) {
                setFullFIR(response.data);
                return response.data;
            }

            showErrorPopup("Error fetching case record. Please try again.");
            return null;
        } catch (error) {
            const status = error?.response?.status;
            if (status === 401) {
                showErrorPopup("Session Expired. Please log in again.");
                goToLogin();
                return null;
            }
            console.error("Error fetching full FIR:", error);
            showErrorPopup("Error processing request. Please try after sometime.");
            return null;
        } finally {
            setIsLoading(false);
            setIsLoadingFIR(false);
        }
    };

    useEffect(() => {
        if (!fullFIR) {
            getFullFIR();
        }
    }, []);

    useEffect(() => {
        if (location.state?.fullFIR) {
            setFullFIR(location.state.fullFIR);
            setComplainantName(location.state.fullFIR.complainant?.name || "");
            setCaseType(location.state.fullFIR.caseType || "");
        }

        if (location.state?.fir) {
            setComplainantName(location.state.fir.complainant?.name || "");
            setCaseType(location.state.fir.caseType || "");
        }
    }, [location.state]);

    const handleCSOption = () => {
        setShowCSConfirmation(false);
        if (csAction === "generate") {
            navigate("/fir/casediary/upload", { state: { fir: fullFIR } });
        } else if (csAction === "verify") {
            navigate("/fir/chargesheet/upload", { state: { fir: fullFIR } });
        }
    }

    const viewComplaint = async () => {
        if (!fullFIR) {
            setIsLoadingComplaint(true);
            await getFullFIR();
        }
        setIsLoadingComplaint(false);
        setIsViewComplaintPopupOpen(true);
    }


    const callApi = async (endpoint) => {
        setLoadingButton(endpoint);

        try {
            let data = fullFIR;
            if (!data) {
                data = await getFullFIR();
            }
            if (!data) return;

            if (
                endpoint === "events" &&
                Array.isArray(data.eventsAndTimeline) &&
                data.eventsAndTimeline.length > 0
            ) {
                navigate("/fir/timeline", {
                    state: { fir: data, section: "timeline" },
                });
                return;
            }

            if (
                endpoint === "tasks" &&
                Array.isArray(data.urgentTasks) &&
                data.urgentTasks.length > 0
            ) {
                navigate("/fir/tasks", { state: { fir: data, section: "tasks" } });
                return;
            }

            if (
                endpoint === "evidences" &&
                data.evidenceList &&
                typeof data.evidenceList === "object" &&
                Object.keys(data.evidenceList).length > 0
            ) {
                navigate("/fir/evidences", { state: { fir: data, section: "evidences", } });
                return;
            }

            if (
                endpoint === "witnesses" &&
                Array.isArray(data.witnessList) &&
                data.witnessList.length > 0
            ) {
                navigate("/fir/witnesses", { state: { fir: data, section: "witnesses" } });
                return;
            }

            if (endpoint === "interrogations") {
                navigate("/fir/accused", { state: { fir: data, section: "accused" } });
                return;
            }

            if (endpoint === "statements") {
                const responses =
                    data.investigation &&
                        Array.isArray(data.investigation.responses) &&
                        data.investigation.responses.length > 0
                        ? data.investigation.responses
                        : [];
                // If responses is empty, navigate to statements
                if (responses.length === 0) {
                    navigate("/fir/statement", { state: { id: data.id } });
                    return;
                }
                navigate("/fir/statements", { state: { fir: data, section: "statements" } });
                return;
            }

            if (endpoint === "discrepancies") {
                let contradictions =
                    data.investigation &&
                        Array.isArray(data.investigation.contradictions) &&
                        data.investigation.contradictions.length > 0
                        ? data.investigation.contradictions
                        : [];
                let inconsistencies =
                    data.investigation &&
                        Array.isArray(data.investigation.inconsistencies) &&
                        data.investigation.inconsistencies.length > 0
                        ? data.investigation.inconsistencies
                        : [];

                if (contradictions.length === 0) contradictions = ["No contradictions found yet."];
                if (inconsistencies.length === 0) inconsistencies = ["No inconsistencies found yet."];

                navigate("/fir/discrepancies", { state: { fir: data, section: "discrepancies" } });
                return;
            }

            if (endpoint === "chargesheet") {
                if (data.caseDiary && data.caseDiary.bucketFileName) {
                    navigate("/fir/chargesheet/generated", { state: { fir: data } });
                } else {
                    navigate("/fir/casediary/upload", { state: { fir: data } });
                }

                /*
                if ((data.caseDiary && data.caseDiary.uploadedOn) || (data.chargeSheet && data.chargeSheet.uploadedOn)) {
                    navigate("/fir/chargesheet", { state: { fir: data } });
                } else {
                    setShowCSConfirmation(true);
                }
                */
                return;
            }

            // Call the API to process the request 
            setIsLoading(true);

            if (
                endpoint === "events" ||
                endpoint === "tasks" ||
                endpoint === "evidences" ||
                endpoint === "witnesses" ||
                endpoint === "interrogations" ||
                endpoint === "statements" ||
                endpoint === "discrepancies"
            ) {
                const jwtToken = localStorage.getItem("jwtToken");
                if (!jwtToken) {
                    showErrorPopup("Session Expired. Please log in again.");
                    goToLogin();
                    return;
                }

                const response = await axios.get(`${API_URL}/fir/${endpoint}/${firId}`, {
                    headers: { Authorization: `Bearer ${jwtToken}` },
                });

                if (response.status === 200) {
                    setFullFIR(response.data);
                    data = response.data;
                }
            }

            switch (endpoint) {
                case "events":
                    navigate("/fir/timeline", { state: { fir: data, section: "timeline", from: "Dashboard" } });
                    break;
                case "tasks":
                    navigate("/fir/tasks", { state: { fir: data, section: "tasks", from: "Dashboard" } });
                    break;
                case "evidences":
                    navigate("/fir/evidences", { state: { fir: data, section: "evidences", from: "Dashboard" } });
                    break;
                case "witnesses":
                    navigate("/fir/witnesses", { state: { fir: data, section: "witnesses", from: "Dashboard" } });
                    break;
                case "interrogations":
                    navigate("/fir/accused", { state: { fir: data, section: "accused", from: "Dashboard" } });
                    break;
                case "statements":
                    navigate("/fir/statements", { state: { fir: data, section: "statements", from: "Dashboard" } });
                    break;
                case "discrepancies":
                    navigate("/fir/discrepancies", { state: { fir: data, section: "discrepancies", from: "Dashboard" } });
                    break;
                default:
                    break;
            }
        } catch (err) {
            console.error("Error calling API:", err);
            const status = err?.response?.status;
            if (status === 401) {
                showErrorPopup("Session Expired. Please log in again.");
                goToLogin();
                return;
            }
            showErrorPopup("Request failed. Please try again.");
        } finally {
            setLoadingButton(null);
            setIsLoading(false);
        }
    };

    const [confirmTimeline, setConfirmTimeline] = useState(false);

    return (
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <Navbar />

            <main
                style={{
                    flex: "1",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f9f9f9",
                    padding: "16px",
                }}
            >
                <div className="info-section">
                    <center>
                        <div style={{ color: '#c62828', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', marginTop: '-40px' }}>
                            You are using the free version. For a more detailed analysis, Monthly and Annual paid subscriptions are available for Police Stations and District Police. Contact 9582822225 for pricing details.
                        </div>
                    </center>
                </div>
                <h2>Case Dashboard</h2>
                <div
                    style={{
                        padding: "20px",
                        width: "100%",
                        maxWidth: "900px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        boxSizing: "border-box",
                    }}
                >
                    {/* <div style={{ textAlign: "left", marginBottom: 12 }}>
                        <div style={{ fontSize: 18, fontWeight: 600, color: "#333" }}>
                            {complainantName}
                        </div>
                        <div style={{ fontSize: 15, color: "#555", marginTop: 6 }}>
                            <span style={{ fontWeight: "bold", color: "#8C1007" }}>
                                {caseType}
                            </span>
                        </div>
                    </div> */}

                    <div style={{ textAlign: "left", marginBottom: 12 }}>
                        <div style={{ fontSize: 18, fontWeight: 600, color: "#333" }}>
                            {complainantName}
                        </div>

                        <div
                            style={{
                                fontSize: 15,
                                color: "#555",
                                marginTop: 6,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                            }}
                        >
                            <span style={{ fontWeight: "bold", color: "#8C1007" }}>
                                {caseType}
                            </span>

                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        getFullFIR();
                                    }}
                                    style={{
                                        color: "#007AFF",
                                        fontWeight: 600,
                                        textDecoration: "none",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Refresh
                                </a>
                                <span style={{ color: "#ccc" }}>|</span>
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        viewComplaint();
                                    }}
                                    style={{
                                        color: "#007AFF",
                                        fontWeight: 600,
                                        textDecoration: "none",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    View Complaint
                                </a>
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            height: 1,
                            backgroundColor: "#e0e0e0",
                            margin: "10px 0 25px",
                        }}
                    />

                    {/* ✅ GRID ROWS */}
                    {/* ✅ Row 1: 4 buttons */}
                    <div className="gridRow">
                        <DashboardButton
                            label="Case Brief"
                            icon={<MdTimeline size={32} color="#094268" />}
                            // onClick={() => callApi("events")}
                            onClick={() => handleSectionClick("events")}
                            loading={loadingButton === "events"}
                            disabled={disableIfOtherRunning("events")}
                            count={fullFIR && fullFIR.eventsAndTimeline && fullFIR.eventsAndTimeline.length > 0 ? fullFIR.eventsAndTimeline.length : 0}
                        />
                        <DashboardButton
                            label="SOPs"
                            icon={<MdChecklist size={32} color="#094268" />}
                            //onClick={() => callApi("tasks")}
                            onClick={() => handleSectionClick("tasks")}
                            loading={loadingButton === "tasks"}
                            disabled={disableIfOtherRunning("tasks")}
                            count={fullFIR && fullFIR.urgentTasks && fullFIR.urgentTasks.length > 0 ? fullFIR.urgentTasks.length : 0}
                        />
                        <DashboardButton
                            label="Evidences"
                            icon={<FaFolderOpen size={30} color="#094268" />}
                            //onClick={() => callApi("evidences")}
                            onClick={() => handleSectionClick("evidences")}
                            loading={loadingButton === "evidences"}
                            disabled={disableIfOtherRunning("evidences")}
                            count={(() => {
                                const c = Object.keys(fullFIR?.evidenceList ?? {}).length;
                                return c > 0 ? c : 0;
                            })()}
                        />
                        <DashboardButton
                            label="Witnesses"
                            icon={<FaUsers size={30} color="#094268" />}
                            //onClick={() => callApi("witnesses")}
                            onClick={() => handleSectionClick("witnesses")}
                            loading={loadingButton === "witnesses"}
                            disabled={disableIfOtherRunning("witnesses")}
                            count={fullFIR && fullFIR.witnessList && fullFIR.witnessList.length > 0 ? fullFIR.witnessList.length : 0}
                        />
                    </div>

                    {/* ✅ Row 2: 4 buttons */}
                    <div className="gridRow" style={{ marginBottom: 10 }}>
                        <DashboardButton
                            label="Accused"
                            icon={<FaUserShield size={30} color="#094268" />}
                            //onClick={() => callApi("interrogations")}
                            onClick={() => handleSectionClick("interrogations")}
                            loading={loadingButton === "interrogations"}
                            disabled={disableIfOtherRunning("interrogations")}
                            count={fullFIR && fullFIR.accusedList && fullFIR.accusedList.length > 0 ? fullFIR.accusedList.length : 0}
                        />
                        <DashboardButton
                            label="Statements"
                            icon={<MdDescription size={32} color="#094268" />}
                            //onClick={() => callApi("statements")}
                            onClick={() => handleSectionClick("statements")}
                            loading={loadingButton === "statements"}
                            disabled={disableIfOtherRunning("statements")}
                            count={fullFIR && fullFIR.investigation && fullFIR.investigation.responses && fullFIR.investigation.responses.length > 0 ? fullFIR.investigation.responses.length : 0}
                        />
                        <DashboardButton
                            label="Discrepancies"
                            icon={<MdSearch size={32} color="#094268" />}
                            //onClick={() => callApi("discrepancies")}
                            onClick={() => handleSectionClick("discrepancies")}
                            loading={loadingButton === "discrepancies"}
                            disabled={disableIfOtherRunning("discrepancies")}
                            count={
                                (() => {
                                    const c = fullFIR?.investigation?.contradictions?.length ?? 0;
                                    const i = fullFIR?.investigation?.inconsistencies?.length ?? 0;
                                    return c + i;
                                })()
                            }
                        />
                        <DashboardButton
                            label="Chargesheet"
                            icon={<MdBalance size={32} color="#094268" />}
                            // onClick={() => callApi("chargesheet")}
                            onClick={() => handleSectionClick("chargesheet")}
                            loading={loadingButton === "chargesheet"}
                            disabled={disableIfOtherRunning("chargesheet")}
                            count={fullFIR && fullFIR.caseDiary && fullFIR.caseDiary.bucketFileName ? 1 : 0}
                        />
                    </div>

                    {isLoading && (
                        <div style={{ marginTop: 16, textAlign: "center", color: "#555" }}>
                            Please wait. It may take about a minute to analyze the case...
                        </div>
                    )}

                    {isLoadingFIR && (
                        <div className="loader-container">
                            <div className="loader"></div>
                        </div>
                    )}

                    {showCSConfirmation && (
                        <div className="popup-overlay">
                            <div className="popup-container">
                                <h2>Generate or Verify Charge Sheet</h2>
                                <p>Generate charge sheet from the case diary, or verify an existing charge sheet?</p>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "30px",
                                        margin: "10px 0 15px",
                                        alignItems: "center",
                                        alignContent: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        <input
                                            type="checkbox"
                                            checked={csAction === "generate"}
                                            onChange={() => setCsAction("generate")}
                                        />
                                        Generate
                                    </label>

                                    <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        <input
                                            type="checkbox"
                                            checked={csAction === "verify"}
                                            onChange={() => setCsAction("verify")}
                                        />
                                        Verify
                                    </label>
                                </div>

                                <div className="popup-buttons">
                                    <button
                                        className="cancel-button"
                                        onClick={() => setShowCSConfirmation(false)}
                                    >Cancel</button>
                                    <button
                                        className="submit-popup-button"
                                        disabled={!csAction}
                                        onClick={handleCSOption}
                                    >Submit</button>
                                </div>
                                {/* <h5>Please email contact@legalapp.in to get the analysis voucher.</h5> */}
                            </div>
                        </div>
                    )}

                    {isViewComplaintPopupOpen && fullFIR && fullFIR.firText && (
                        <div className="popup-overlay">
                            <div className="popup-container">
                                <h3>Complaint Details</h3>
                                <div style={{ fontSize: '16px', alignContent: 'justify', alignItems: 'justify', textAlign: 'justify', maxHeight: '50vh', overflowY: 'auto', marginBottom: '16px' }}>
                                    <p>{fullFIR.firText.split('\n').map((line, index) => (
                                        <span key={index}>
                                            {line}
                                            <br />
                                        </span>
                                    ))}</p>
                                </div>
                                <div className="popup-buttons">
                                    <button
                                        className="submit-popup-button"
                                        onClick={() => {
                                            setIsViewComplaintPopupOpen(false);
                                        }
                                        }>Close</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {confirmSection.open && (
                        <div className="popup-overlay">
                            <div className="popup-container">
                                <h3>{confirmSection.title}</h3>

                                <div
                                    style={{
                                        fontSize: "16px",
                                        textAlign: "justify",
                                        maxHeight: "50vh",
                                        overflowY: "auto",
                                        marginBottom: "16px",
                                    }}
                                >
                                    {confirmSection.text}
                                    <br />
                                    <br />
                                    {CONTINUE_PROMPT_I18N[getPreferredLang()] || CONTINUE_PROMPT_I18N.en}
                                </div>

                                <div className="popup-buttons">
                                    <button className="cancel-button" onClick={closeSectionPopup}>
                                        Cancel
                                    </button>
                                    <button className="submit-popup-button" onClick={confirmSectionAndProceed}>
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}


                    {isLoadingComplaint && (
                        <div
                            style={{
                                position: "fixed",
                                inset: 0,
                                backgroundColor: "rgba(255,255,255,0.6)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 9999,
                            }}
                        >
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                                <div className="spinner" />
                                <div style={{ color: "#555", fontWeight: 600 }}>Loading complaint…</div>
                            </div>
                        </div>
                    )}


                    {showError && errorMessage && (
                        <div className="popup-overlay">
                            <div className="popup-container">
                                <h3>Error</h3>
                                <p>{errorMessage}</p>
                                <button className="submit-popup-button" onClick={handleCloseError}>
                                    OK
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Back to My Cases link (left-aligned to the same container as the buttons box) */}
                <div
                    style={{
                        width: "100%",
                        maxWidth: "900px",
                        marginTop: "14px",
                        display: "flex",
                        justifyContent: "flex-start",
                        boxSizing: "border-box",
                        paddingLeft: "4px", // optional tiny inset
                    }}
                >
                    <span
                        onClick={() => navigate('/mycases')}
                        style={{
                            cursor: "pointer",
                            color: "#007AFF",
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "16px",
                        }}
                    >
                        <span aria-hidden="true" style={{ fontSize: "18px", lineHeight: "14px" }}>
                            ←
                        </span>
                        My Cases
                    </span>
                </div>


            </main>

            <Footer />
        </div>
    );
};

export default InvestigationDashboardPage;
